import "./style.scss";

import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ModalContent } from "@amzn/stencil-react-components/modal";
import { DetailedRadio } from "@amzn/stencil-react-components/radio";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Label, Text } from "@amzn/stencil-react-components/text";
import Button from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import Link from "@amzn/uxm-stencil-react-components/lib/src/stencil/link";
import TextArea from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { ApprovalsListForModal } from "common/components/Approvals/ApprovalsListForModal";
import { InlineUserReference } from "common/components/InlineUserReference";
import { License } from "common/components/LicenseSelect/LicenseSelect";
import { CreateOrderRequestContent } from "common/types";
import { PreCheckoutInformation } from "common/types";
import { MessageStruct } from "common/types/ui-types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { UserPermissions } from "lib/services";
import _ from "lodash";
import React, { FunctionComponent, useEffect, useState } from "react";

import { AuditTemp } from "../..";
import { AcknowledgmentInfo, AcknowledgmentsList } from "../Acknowledgments";
import { MessagesList } from "../MessagesList";
import { useSecurityOrderState } from "./security-form-input";
import { SecurityDataForm } from "./SecurityDataForm";

const ACK_USE_POLICY: AcknowledgmentInfo = {
  id: "checkReadAcceptableUsePolicy",
  input: "securityPoliciesConfirmIsChecked",
  dataCy: "read-acceptable-use-policy-checkbox",
  policies: [
    getString("submitSecurityApprovalOrder.policy2.name"),
    getString("submitSecurityApprovalOrder.policy3.name"),
  ],
  text: (
    <Text>
      {getString("submitSecurityApprovalOrder.acceptConditions.part2")}
      <Link
        href={getString("submitSecurityApprovalOrder.policy2.url")}
        touchpoint="acceptable-use-policy-link"
        target="_blank"
      >
        {getString("submitSecurityApprovalOrder.policy2.name")}
      </Link>
      {getString("submitSecurityApprovalOrder.acceptConditions.part3")}
      <Link
        href={getString("submitSecurityApprovalOrder.policy3.url")}
        touchpoint="third-party-software-policy-link"
        target="_blank"
      >
        {getString("submitSecurityApprovalOrder.policy3.name")}
      </Link>
      {getString("submitSecurityApprovalOrder.acceptConditions.part4")}
    </Text>
  ),
};

const ACK_DATA_CLASSIFICATION: AcknowledgmentInfo = {
  id: "checkReadSecurityData",
  dataCy: "read-high-sensitive-security-data-policy-checkbox",
  input: "dataClassificationConfirmIsChecked",
  policies: [getString("submitSecurityApprovalOrder.policy1.name")],
  text: (
    <Text>
      {getString("submitSecurityApprovalOrder.acceptConditions.part1")}
      {getString("submitSecurityApprovalOrder.acceptConditions.part4")}
    </Text>
  ),
};

export interface SubmitSecurityOrderModalProps {
  name: string;
  taxonomyId: string;
  submitOrderHandler: (request: CreateOrderRequestContent) => void;
  preCheckoutInformation?: PreCheckoutInformation;
  close: () => void;
  csrfTokenIsLoading: boolean;
  isLoadingCreate: boolean;
  messages?: MessageStruct[];
  licenseInfo?: License;
  isOsCompatible?: boolean;
}

export const SubmitSecurityOrderModal: FunctionComponent<SubmitSecurityOrderModalProps> = ({
  name,
  taxonomyId,
  preCheckoutInformation,
  submitOrderHandler,
  close,
  csrfTokenIsLoading,
  isLoadingCreate,
  messages,
  licenseInfo,
  isOsCompatible,
}: SubmitSecurityOrderModalProps) => {
  const { inputs, updateInputs } = useSecurityOrderState();
  const userAlias = UserPermissions.current.username;
  const [acknowledgementAudit, setAcknowledgementAudit] = useState<AuditTemp>({});
  const [acksList, setAckList] = useState<AcknowledgmentInfo[] | undefined>();
  const [acksAreReady, setAcksAreReady] = useState(false);

  useEffect(() => {
    if (inputs.goNext) {
      updateInputs("showForm", false);
    } else {
      updateInputs("showForm", true);
    }
  }, [inputs.goNext]);

  useEffect(() => {
    updateInputs(
      "securitySummary",
      `I already have an approval, URL: <a target="_blank" href="${inputs.securityUrl}" style="color: #1768C9;">${inputs.securityUrl}</a>`
    );
  }, [inputs.securityUrl]);

  const submitOrder = async () => {
    const request: CreateOrderRequestContent = {
      items: {
        [taxonomyId!]: {
          quantity: 1,
          details: {
            softwareOrderDetails: { securitySummary: inputs.securitySummary },
            ...(licenseInfo !== undefined && { licenseInfo }),
          },
        },
      },
      comment: inputs.comment,
      acknowledgementAudit: _.values(acknowledgementAudit),
    };
    await submitOrderHandler(request);
  };

  const handleFormSave = (formData: string) => {
    updateInputs("securitySummary", formData);
  };

  const handleFormSubmit = (enableSubmit: boolean) => {
    updateInputs("formIsValid", enableSubmit);
  };

  const handleConfirmCheck = (policies: string[], ack: boolean, input?: string) => {
    if (input) {
      updateInputs(input, ack); // update use state variable
    }
    for (let i = 0; i < policies.length; i++) {
      const policyName = policies[i];
      acknowledgementAudit[policyName] = {
        by: userAlias!,
        on: new Date(),
        policyName: policyName,
        acknowledged: ack,
      };
    }
  };

  const canClickSubmmit = () => {
    const securityURLAdded = inputs.securityUrl.trim().length > 5;
    const canAutoApprove = preCheckoutInformation !== undefined && preCheckoutInformation!.canBeAutoApproved;
    const submitSecurityURL = !inputs.showForm && securityURLAdded;
    const submitSecurityForm = inputs.showForm && inputs.formIsValid;
    const hasSecurityData = submitSecurityURL || submitSecurityForm;

    if (!acksAreReady || isLoadingCreate) return false;
    if (canAutoApprove && hasSecurityData) return true;
    return inputs.comment != undefined && inputs.comment.trim() && hasSecurityData;
  };

  const canClickNext = () => {
    const hasPreCheckoutData = preCheckoutInformation !== undefined;
    const canAutoApprove = hasPreCheckoutData && preCheckoutInformation!.canBeAutoApproved;
    const hasApproveJustification =
      hasPreCheckoutData && !canAutoApprove && inputs.comment != undefined && inputs.comment.trim();
    if (!acksAreReady || isLoadingCreate || (!canAutoApprove && !hasApproveJustification)) return false;
    return true;
  };

  const selectApprovalRequestOption = (
    <Col>
      <Row>
        <Text>{getString("submitSecurityApprovalOrder.approvalSelectionText")}</Text>
      </Row>
      <Row gridGap="S300">
        <DetailedRadio
          data-testid="radio-prev-approval"
          titleText="Already have an approval link"
          checked={inputs.hasApproval}
          onChange={() => updateInputs("hasApproval", true)}
        />
        <DetailedRadio
          data-testid="radio-new-approval"
          titleText="Create an approval request"
          checked={!inputs.hasApproval}
          onChange={() => updateInputs("hasApproval", false)}
        />
      </Row>
    </Col>
  );

  const securityApprovalUrl = (
    <Col padding="L400">
      <Label htmlFor="approval-url-text-area">
        {getString("submitSecurityApprovalOrder.submitApplinkSecurityOrder.approvalURLDescriptionBody")}
      </Label>
      <TextArea
        id="approval-url-text-area"
        data-cy="approval-url-text-area"
        touchpoint="approval-url-text-area"
        placeholder={
          getString("submitSecurityApprovalOrder.submitApplinkSecurityOrder.approvalURLDescriptionPlaceholder.part1") +
          name +
          getString("submitSecurityApprovalOrder.submitApplinkSecurityOrder.approvalURLDescriptionPlaceholder.part2")
        }
        value={inputs.securityUrl}
        onChange={(e) => updateInputs("securityUrl", e.target.value)}
      />
    </Col>
  );

  const modalButtons = (
    <div key="action-buttons">
      {inputs.hasApproval || (!inputs.hasApproval && inputs.showForm) ? (
        <Button
          key="submitOrder-submit"
          data-testid="submitOrder-submit"
          touchpoint="security-modal-submit-order-button"
          data-cy="submit-order-button"
          variant={ButtonVariant.Primary}
          disabled={!canClickSubmmit()}
          onClick={submitOrder}
        >
          Submit
          {isLoadingCreate ? <Spinner size={SpinnerSize.Small} fr={undefined} /> : null}
        </Button>
      ) : (
        <Button
          key="next-button"
          data-testid="next-button"
          touchpoint="security-modal-next-button"
          data-cy="next-button"
          variant={ButtonVariant.Primary}
          disabled={!canClickNext()}
          onClick={() => updateInputs("goNext", false)}
        >
          Next
        </Button>
      )}
    </div>
  );

  const selectModalBodyComponent = () => {
    if (!preCheckoutInformation || csrfTokenIsLoading) {
      return (
        <>
          <Spinner size={SpinnerSize.Medium} fr={undefined} />
          <br />
        </>
      );
    }

    if (preCheckoutInformation.canBeAutoApproved) {
      return (
        <>
          <Text color="neutral90" fontSize="T300">
            {getString("submitSanctionOrder.autoApproveOrderBody")}
          </Text>
          <br />
        </>
      );
    }

    const requiredApprover = preCheckoutInformation.requiredSTPApprover;

    return (
      <>
        <ApprovalsListForModal requiredApprovals={preCheckoutInformation.requiredApprovals}></ApprovalsListForModal>

        {requiredApprover && (
          <Text color="neutral90" fontSize="T200">
            {getString("submitSanctionOrder.managerRequest.body")}
            <strong>
              {" "}
              <InlineUserReference userReference={requiredApprover}></InlineUserReference>{" "}
            </strong>
          </Text>
        )}
        <Label htmlFor="comment-text-area">{getString("submitSanctionOrder.managerRequest.textAreaLabel")}</Label>
        <TextArea
          id="comment-text-area"
          data-cy="comment-text-area"
          touchpoint="comment-text-area"
          placeholder={getString("submitSanctionOrder.managerRequest.textAreaPlaceHolder")}
          value={inputs.comment}
          onChange={(e) => updateInputs("comment", e.target.value)}
          style={{ width: "100%" }}
        />
        <br />
      </>
    );
  };

  const modalBodyComponent = selectModalBodyComponent();

  // Builds the ACK list
  useEffect(() => {
    const list: AcknowledgmentInfo[] = [];
    if (preCheckoutInformation?.acksList) {
      preCheckoutInformation.acksList.forEach((item) => list!.push(item));

      /* Data Classification Acknowledgement */
      list.push(ACK_DATA_CLASSIFICATION);

      /* Acceptable Use Policy and Third Party Use Policy */
      list.push(ACK_USE_POLICY);
    }
    setAckList(list);
  }, [preCheckoutInformation?.acksList]);

  const nonCompatibleOsDisclaimerQuery = (
    <>
      <Col gridGap="S200" alignSelf="flex-start" width={"100%"}>
        <MessageBanner type={MessageBannerType.Warning} dataTestId="os-disclaimer-query">
          {getString("nonCompatibleOS.disclaimerInformation")}
        </MessageBanner>
      </Col>
    </>
  );

  const createModalContent = (
    <>
      <Col gridGap="S200" alignSelf="flex-start">
        {modalBodyComponent}
      </Col>
      <Col gridGap="S400" alignItems="start">
        {!isOsCompatible && nonCompatibleOsDisclaimerQuery}
        <Col gridGap="S200" alignSelf="flex-start">
          <MessageBanner type={MessageBannerType.Informational}>
            {getString("submitSecurityApprovalOrder.submitApplinkSecurityOrder.dataClassificationInformation")}
          </MessageBanner>
        </Col>
      </Col>
      <Col rowGap="S200">
        <AcknowledgmentsList list={acksList} handleSingleCheck={handleConfirmCheck} handleReady={setAcksAreReady} />
      </Col>
      {selectApprovalRequestOption}
      <br />
      {inputs.hasApproval && securityApprovalUrl}
      <Col gridGap="S200" alignSelf="flex-start">
        <Text fontSize="T100" color="neutral70">
          {getString("submitSanctionOrder.requiredField")}
        </Text>
      </Col>
    </>
  );

  return (
    <ModalContent
      titleText={name ? name : ""}
      buttons={[
        <Button
          key="submitOrder-close"
          touchpoint="security-modal-cancel-button"
          variant={ButtonVariant.Tertiary}
          onClick={close}
        >
          Cancel
        </Button>,
        modalButtons,
      ]}
      maxWidth={815}
    >
      <Col>
        <MessagesList messages={messages}></MessagesList>
      </Col>

      {inputs.showForm ? (
        <SecurityDataForm
          onSave={handleFormSave}
          onValidationSubmit={handleFormSubmit}
          softwareName={name ? name : ""}
        />
      ) : (
        createModalContent
      )}
    </ModalContent>
  );
};
